import { useOutletContext } from "react-router";

import { ExistingDestination, prepareDestinationFromExisting, useDestinationForm } from "@prequel/react";
import { DescriptionList, Spinner } from "@prequel/shared-components";

import { useOrg } from "@/api/queries/orgs";
import { useRecipient } from "@/api/queries/recipients";
import Timestamp from "@/components/Timestamp";
import VendorLogo from "@/components/VendorLogo";
import { env } from "@/env";
import { getReadableLastSuccessfulSync, getReadableModels, hiddenSecretValue, isSecretField } from "@/types";

type DestinationDetailsProps = {
  destination: ExistingDestination;
};

const DestinationDetails = () => {
  const { destination: existingDestination } = useOutletContext<DestinationDetailsProps>();
  const { data: recipient } = useRecipient(existingDestination?.recipient_id);
  const { data: org } = useOrg();

  const destination = prepareDestinationFromExisting(existingDestination);
  const form = useDestinationForm(destination, org?.id ?? "", {
    includeInternalFields: true,
    host: env.VITE_APP_API_SERVER,
  });

  if (!recipient || !form || !org) {
    return <Spinner />;
  }

  const vendor =
    form[0].fields[0].form_element === "select"
      ? form[0].fields[0].enum?.find(({ key }) => key === destination.vendor)
      : undefined;
  return (
    <>
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1 pb-6">
          <h3 className="text-lg/6 font-medium text-gray-900">Status</h3>
        </div>
        <div>
          <DescriptionList>
            <DescriptionList.Item
              label="Last Successful Transfer"
              value={getReadableLastSuccessfulSync(existingDestination?.last_successful_transfer_ended_at ?? undefined)}
            />

            <DescriptionList.Item label="Current Status" value={destination?.is_enabled ? "Enabled" : "Disabled"} />
          </DescriptionList>
        </div>
      </div>
      {/* Don't need to block entire page on recipient loading so conditionally rendering here */}
      {recipient && (
        <div className="mt-10 divide-y divide-gray-200">
          <div className="space-y-1">
            <h3 className="text-lg/6 font-medium text-gray-900">Recipient</h3>
          </div>
          <div className="mt-6">
            <DescriptionList>
              <DescriptionList.Item label="Name" value={recipient.name} />
              <DescriptionList.Item label="ID in provider system" value={recipient.id_in_provider_system} />
              <DescriptionList.Item label="Products" value={recipient.products.join(", ")} />
            </DescriptionList>
          </div>
        </div>
      )}
      {/* Description list */}
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1 pb-6">
          <h3 className="text-lg/6 font-medium text-gray-900">Details</h3>
        </div>
        <div>
          {form && destination && (
            <DescriptionList>
              <DescriptionList.Item label="ID" value={existingDestination.id} />
              {vendor && (
                <DescriptionList.Item
                  label="Vendor"
                  value={
                    <div className="flex items-center">
                      <VendorLogo logo_url={vendor.icon_url ?? ""} />
                      {vendor.display}
                    </div>
                  }
                />
              )}
              <DescriptionList.Item label="Enabled" value={destination.is_enabled ? "Yes" : "No"} />
              {form.map((section) =>
                section.fields.map((field) => {
                  if (isSecretField(field)) {
                    return (
                      field.label && (
                        <DescriptionList.Item key={field.name} label={field.label} value={hiddenSecretValue} />
                      )
                    );
                  }

                  if (field.name === "public_key" || field.name === "pgp_public_key") {
                    return (
                      <DescriptionList.CodeBlockItem
                        key={field.name}
                        label={field.label}
                        value={destination[field.name]}
                      />
                    );
                  }

                  if (field.name === "frequency_minutes") {
                    return (
                      <DescriptionList.Item
                        key={field.name}
                        label={field.label}
                        value={destination[field.name] === 0 ? "Default org frequency" : destination[field.name]}
                      />
                    );
                  }

                  if (field.name === "use_ssh_tunnel") {
                    return (
                      <DescriptionList.Item
                        key={field.name}
                        label={"Use SSH Tunnel"}
                        value={destination[field.name] ? "Yes" : "No"}
                      />
                    );
                  }

                  if (field.type === "boolean") {
                    return (
                      <DescriptionList.Item
                        key={field.name}
                        label={field.label}
                        value={destination[field.name] ? "Yes" : "No"}
                      />
                    );
                  }

                  // Use enum display value if available
                  let itemValue = destination[field.name];
                  if ((field.form_element === "radio" || field.form_element === "select") && field.enum) {
                    const e = field.enum.find(({ key }) => key === itemValue);
                    if (e) {
                      itemValue = e.display;
                    }
                  }

                  return field.label && <DescriptionList.Item key={field.name} label={field.label} value={itemValue} />;
                }),
              )}
              <DescriptionList.Item label="Enabled models" value={getReadableModels(destination.enabled_models)} />
              {destination.created_at && (
                <DescriptionList.Item label="Created" value={<Timestamp timestamp={destination.created_at} />} />
              )}
              {destination.updated_at && (
                <DescriptionList.Item label="Updated" value={<Timestamp timestamp={destination.updated_at} />} />
              )}
            </DescriptionList>
          )}
        </div>
      </div>
    </>
  );
};

export default DestinationDetails;
